import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { restrictedTimingKeys } from '../constants';
import { logger } from '../logger';
import { visibilityChangeObserver } from '../observer/visibility-change-observer';
import { UntilExperience } from './until-experience';
export let MetricState;

(function (MetricState) {
  MetricState["NOT_STARTED"] = "not-started";
  MetricState["STARTED"] = "started";
  MetricState["FINISHED"] = "finished";
  MetricState["CANCELLED"] = "cancelled";
})(MetricState || (MetricState = {}));

export let PageVisibleState;

(function (PageVisibleState) {
  PageVisibleState["VISIBLE"] = "visible";
  PageVisibleState["MIXED"] = "mixed";
  PageVisibleState["HIDDEN"] = "hidden";
})(PageVisibleState || (PageVisibleState = {}));

function getPageVisibleState() {
  return document.visibilityState === 'visible' ? PageVisibleState.VISIBLE : PageVisibleState.HIDDEN;
}

export class BaseMetric {
  constructor(config) {
    var _this$config$timings;

    _defineProperty(this, "state", MetricState.NOT_STARTED);

    _defineProperty(this, "startTime", null);

    _defineProperty(this, "stopTime", null);

    _defineProperty(this, "marks", {});

    _defineProperty(this, "onStopCallbacks", []);

    _defineProperty(this, "custom", null);

    _defineProperty(this, "submetrics", []);

    _defineProperty(this, "route", null);

    _defineProperty(this, "untilOnStopCallback", null);

    _defineProperty(this, "pageVisibleState", getPageVisibleState());

    _defineProperty(this, "setPageVisibleStateToMixed", () => {
      this.pageVisibleState = PageVisibleState.MIXED;
    });

    this.config = config;
    this.key = config.key.replace(/ /gi, '-');
    this.type = config.type;
    (_this$config$timings = this.config.timings) === null || _this$config$timings === void 0 ? void 0 : _this$config$timings.forEach(entry => {
      if (restrictedTimingKeys.includes(entry.key)) {
        // eslint-disable-next-line no-console
        console.warn(`Entry ${entry.key} is restricted and has been renamed to custom__${entry.key}`);
        entry.key = `custom__${entry.key}`;
      }
    });
  }

  get untilMetrics() {
    const {
      until
    } = this.config;

    if (!until) {
      return [];
    } else if (Array.isArray(until)) {
      return this.getConfiguredUntilMetricArray(until);
    }

    return this.getConfiguredUntilMetricArray([until]);
  }

  start({
    startTime = window.performance.now()
  } = {}) {
    this.clear();
    logger.logCond(!!this.config.debug, this.key, startTime);
    this.state = MetricState.STARTED;
    this.startTime = startTime;
    this.pageVisibleState = getPageVisibleState();
    visibilityChangeObserver.subscribe(this.setPageVisibleStateToMixed);
    this.watchUntil();
  }

  stop(stopArguments = {}) {
    const {
      stopTime = window.performance.now()
    } = stopArguments;

    if (this.state !== MetricState.STARTED || this.startTime === null) {
      logger.log(`metric ${this.config.key} has been stopped while not being started before; current state: ${this.state}`);
      return false;
    }

    if (stopTime < this.startTime) {
      logger.log(`metric ${this.config.key} has been stopped with stopTime lower than startTime; startTime: ${this.startTime}; stopTime: ${stopTime}`);
      return false;
    }

    this.stopTime = stopTime;
    this.state = MetricState.FINISHED;
    this.handleStop(stopArguments);
    visibilityChangeObserver.unsubscribe(this.setPageVisibleStateToMixed);
    return true;
  }

  handleStop(stopArguments) {
    const {
      customData = null
    } = stopArguments;

    if (customData) {
      this.custom = { ...this.custom,
        ...customData
      };
    }

    this.config.include && this.config.include.forEach(metric => {
      const included = metric.getData();

      if (included.state === MetricState.FINISHED && included.start !== null && included.start >= (this.startTime || 0)) {
        this.addSubMetric(metric.getData());
      }
    });
    this.onStopCallbacks.forEach(([success]) => {
      success(stopArguments);
    });
    this.onStopCallbacks = [];
    logger.logCond(!!this.config.debug, this.key, this.config.debug && this.getData());
  }

  mark(mark, timestamp = window.performance.now()) {
    this.marks[mark] = timestamp;
  }

  cancel() {
    if (this.state !== MetricState.STARTED) {
      return;
    }

    this.state = MetricState.CANCELLED;
    this.cancelUntil();
    this.onStopCallbacks.forEach(([, cancel]) => {
      cancel();
    });
    this.onStopCallbacks = [];
    visibilityChangeObserver.unsubscribe(this.setPageVisibleStateToMixed);
  }

  merge(mergeData) {
    this.state = mergeData.state;
    this.pageVisibleState = mergeData.pageVisibleState;
    this.startTime = mergeData.start;
    this.stopTime = mergeData.stop;
    this.marks = { ...this.marks,
      ...mergeData.marks
    };
    this.submetrics = [...this.submetrics, ...mergeData.submetrics];
    this.onStopCallbacks = [...this.onStopCallbacks, ...mergeData.onStopCallbacks];
  }

  onStop(success, cancel) {
    this.onStopCallbacks.push([success, cancel]);
  }

  removeOnStopCallback(success) {
    const index = this.onStopCallbacks.findIndex(([successCallback]) => {
      return success === successCallback;
    });

    if (index > -1) {
      this.onStopCallbacks.splice(index, 1);
    }
  }

  getData() {
    return {
      key: this.key,
      state: this.state,
      start: this.startTime,
      stop: this.stopTime,
      //todo ranges
      // ranges: this.makeRanges()
      marks: this.marks,
      // todo make copy
      custom: this.custom,
      submetrics: this.submetrics,
      config: this.config,
      route: this.route,
      pageVisibleState: this.pageVisibleState,
      type: this.config.type
    };
  }

  getDataToMerge() {
    return { ...this.getData(),
      onStopCallbacks: this.onStopCallbacks
    };
  }

  addSubMetric(data) {
    this.submetrics.push(data);
  }
  /**
   *
   * @param metric
   * The array of Until metrics can be of two types
   * This function returns an UntilExperience
   */


  getConfiguredUntilMetric(metric) {
    return new UntilExperience(metric);
  }

  getConfiguredUntilMetricArray(metricArray) {
    if (!metricArray.length) {
      return [];
    }

    return metricArray.map(metric => this.getConfiguredUntilMetric(metric));
  }

  isUntilFinished() {
    return this.untilMetrics.every(metric => metric.experience.getData().state === MetricState.FINISHED);
  }

  handleAllUntilFinished() {
    // Order by ascending stop time
    const sorted = [...this.untilMetrics].sort((m1, m2) => {
      return m1.getDependencyStopTime() - m2.getDependencyStopTime();
    }); // Accumulate the custom data from each metric, later metrics take priority

    const accumulatedCustomData = {};
    sorted.forEach(metric => {
      Object.assign(accumulatedCustomData, metric.experience.getData().custom);
    });
    const latestSegment = sorted[sorted.length - 1];
    this.stop({
      stopTime: latestSegment.getDependencyStopTime(),
      customData: { ...accumulatedCustomData,
        segment: latestSegment.getKey(),
        segmentOverrided: latestSegment.getIsSegmentLoadOverrided()
      }
    });
  }

  watchUntil() {
    if (!this.untilMetrics.length) {
      return;
    }

    if (this.isUntilFinished()) {
      // Already finished, handle it immediately
      this.handleAllUntilFinished();
      return;
    } // If not currently finished, register stop callbacks to re-check


    this.untilOnStopCallback = () => {
      if (this.isUntilFinished()) {
        this.handleAllUntilFinished();
      }
    };

    this.untilMetrics.forEach(metric => {
      if (metric.experience.getData().state !== MetricState.FINISHED) {
        metric.experience.onStop(this.untilOnStopCallback, () => this.cancel());
      }
    });
  }

  cancelUntil() {
    this.untilMetrics.forEach(metric => {
      if (metric.experience.getData().state !== MetricState.CANCELLED && this.untilOnStopCallback) {
        metric.experience.removeOnStopCallback(this.untilOnStopCallback);
      }
    });
  }

  clear() {
    // todo before clearing - handle cancelling existing onStop / unsubscribe submetrics
    this.cancelUntil();
    this.state = MetricState.NOT_STARTED;
    this.startTime = null;
    this.stopTime = null;
    this.marks = {};
    this.custom = null;
    this.submetrics = [];
    this.route = null;
    this.pageVisibleState = getPageVisibleState();
  }

}

_defineProperty(BaseMetric, "FMP", 'fmp');